import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import {
  Box,
  Button,
  Grid,
} from '@mui/material';

import { COUNTRY_DATA, COUNTRY_STATECODE_MAP } from 'utils/constant.js';
import InputField from 'components/Input-Field/input-field';
import SelectField from 'components/Select-Field/select-field';
import CTAutocomplete from 'components/CTAutocomplete';

const LocationForm = ({
  action,
  location,
  isFormModal,
  saveFormHandler,
  updateFormHandler,
  exposeSubmit,
}) => {

  const LocationSchema = Yup.object().shape({
    name: Yup.string().min(2).max(75).required('Location Name is required.'),
    street: Yup.string().max(100).required('Street information is required'),
    city: Yup.string().min(2).max(50).required('City Name is required.'),
    state: Yup.string().min(2).max(25).required('State Name is required.'),
    zipcode: Yup.string().max(10),
    country: Yup.string().max(25).required('Country Name is required.'),
  });

  const [autoAddressInput, setAutoAddressInput] = useState(true);

  const formatPhoneNumber = (e) => {
    if (e.target.name === 'phone') {
      var x = e.target.value
        .replace(/\D/g, '')
        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      e.target.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
      return e.target.value;
    }
  };

  return (
    <Formik
      initialValues={{ ...location }}
      enableReinitialize={true}
      validationSchema={LocationSchema}
      onSubmit={action === 'Save' ? saveFormHandler : updateFormHandler}
    >
      {(formik) => {
        // expose the submitForm to parent via ref
        useEffect(() => {
          exposeSubmit?.(formik.submitForm);
        }, [formik.submitForm]);
        return (
          <Form>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InputField
                  label="Location Name"
                  name="name"
                  value={formik.values.name}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.name && formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InputField
                  label="Location Email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.email && formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InputField
                  label="Location Phone Number"
                  name="phone"
                  type="tel"
                  value={formik.values.phone}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.phone && formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  onBlur={formik.handleBlur}
                  onChange={e => {
                    const phone = formatPhoneNumber(e);
                    formik.setFieldValue('phone', phone);
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }}>
                <CTAutocomplete
                  disabled={!autoAddressInput}
                  fnHandleSelectedLocation={location => {
                    formik.setFieldValue('street', location.addressLabel);
                    formik.setFieldValue('city', location.city);
                    formik.setFieldValue('zipcode', location.postalCode);
                    formik.setFieldValue('state', location.stateCode);
                    formik.setFieldValue('country', location.countryCode);
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="Street"
                  name="street"
                  value={formik.values.street}
                  variant="outlined"
                  fullWidth
                  disabled={autoAddressInput}
                  error={Boolean(formik.touched.street && formik.errors.street)}
                  helperText={formik.touched.street && formik.errors.street}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="City"
                  name="city"
                  value={formik.values.city}
                  variant="outlined"
                  fullWidth
                  disabled={autoAddressInput}
                  error={Boolean(formik.touched.city && formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SelectField
                  label="Select Country"
                  name="country"
                  value={formik.values.country}
                  variant="outlined"
                  fullWidth
                  disabled={autoAddressInput}
                  error={Boolean(formik.touched.country && formik.errors.country)}
                  helperText={formik.touched.country && formik.errors.country}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  options={COUNTRY_DATA.map(data => data.code)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SelectField
                  label="Select State/Province"
                  name="state"
                  select
                  value={formik.values.state}
                  variant="outlined"
                  fullWidth
                  disabled={autoAddressInput}
                  error={Boolean(formik.touched.state && formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  options={formik.values.country !== '' ? COUNTRY_STATECODE_MAP[formik.values.country] : []}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InputField
                  label="Zip code or Postal code"
                  name="zipcode"
                  value={formik.values.zipcode}
                  variant="outlined"
                  fullWidth
                  disabled={autoAddressInput}
                  error={Boolean(formik.touched.zipcode && formik.errors.zipcode)}
                  helperText={formik.touched.zipcode && formik.errors.zipcode}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>
            <Grid />
            {isFormModal &&
              <Box
                display='flex'
                justifyContent='flex-end'
                p={2}>
                {action === 'Save' ?
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                  >
                    Save Location
                  </Button> :
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                  >
                    Update Location
                  </Button>
                }
              </Box>}
          </Form>
        )
      }}
    </Formik>
  );
};
export default LocationForm;